import Papa from "papaparse";

import type { LeadSource } from "../types";
import { phoneKey } from "./leadLogic";

/** What a CSV column can feed (docs/design/Import.dc.html) */
export const IMPORT_TARGETS = [
  "full_name",
  "first_name",
  "last_name",
  "phone",
  "email",
  "created_at",
  "campaign",
  "form",
  "source",
  "rooms",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "comment",
  "ignore",
] as const;

export type ImportTarget = (typeof IMPORT_TARGETS)[number];
export type Mapping = Record<string, ImportTarget>;

/**
 * Decode a file the way spreadsheets and Meta save it: Meta's lead export is
 * UTF-16 with a byte order mark, Excel on Ukrainian Windows writes cp1251.
 */
export const decodeFile = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  if (bytes[0] === 0xff && bytes[1] === 0xfe)
    return new TextDecoder("utf-16le").decode(bytes.subarray(2));
  if (bytes[0] === 0xfe && bytes[1] === 0xff)
    return new TextDecoder("utf-16be").decode(bytes.subarray(2));
  try {
    return new TextDecoder("utf-8", { fatal: true, ignoreBOM: false }).decode(
      bytes,
    );
  } catch {
    return new TextDecoder("windows-1251").decode(bytes);
  }
};

/** Parse CSV text with a comma, semicolon or tab delimiter */
export const parseCsv = (text: string) => {
  const result = Papa.parse<Record<string, string>>(text.trim(), {
    header: true,
    skipEmptyLines: "greedy",
    delimitersToGuess: ["\t", ",", ";"],
    transformHeader: (header) => header.trim(),
  });
  const headers = (result.meta.fields ?? []).filter(Boolean);
  return { headers, rows: result.data };
};

const ALIASES: [ImportTarget, RegExp][] = [
  ["full_name", /^(full_?name|name|піб|ім'?я( та прізвище)?|клієнт)$/],
  ["first_name", /^(first_?name|ім'?я)$/],
  ["last_name", /^(last_?name|прізвище)$/],
  ["phone", /(phone|телефон|номер)/],
  ["email", /(e-?mail|пошта)/],
  ["created_at", /^(created_?time|created_?at|date|дата.*)$/],
  ["campaign", /^(campaign_?name|campaign|кампанія)$/],
  ["form", /^(form_?name|form|форма)$/],
  ["source", /^(platform|source|джерело)$/],
  ["utm_source", /^utm_source$/],
  ["utm_medium", /^utm_medium$/],
  ["utm_campaign", /^utm_campaign$/],
  ["utm_content", /^utm_content$/],
  ["utm_term", /^utm_term$/],
  ["rooms", /(кімнат|квартир|rooms?)/],
  // Meta technical columns
  [
    "ignore",
    /^(id|ad_?id|ad_?name|adset_?id|adset_?name|campaign_?id|form_?id|is_?organic|lead_?status|inbox_url)$/,
  ],
];

/** Best guess for each column; unknown columns are form answers: comment */
export const guessMapping = (headers: string[]): Mapping => {
  const mapping: Mapping = {};
  const used = new Set<ImportTarget>();
  for (const header of headers) {
    const name = header.toLowerCase().replace(/[’`]/g, "'").trim();
    const match = ALIASES.find(
      ([target, pattern]) =>
        pattern.test(name) && (target === "ignore" || !used.has(target)),
    );
    const target = match?.[0] ?? "comment";
    mapping[header] = target;
    if (target !== "ignore" && target !== "comment") used.add(target);
  }
  return mapping;
};

/** Ukrainian numbers to +380XXXXXXXXX; Meta prefixes phones with "p:" */
export const normalizePhone = (raw: string | undefined) => {
  const value = (raw ?? "").replace(/^p:/i, "").trim();
  const digits = value.replace(/\D/g, "");
  if (digits.length === 9) return `+380${digits}`;
  if (digits.length === 10 && digits.startsWith("0")) return `+38${digits}`;
  if (digits.length === 12 && digits.startsWith("380")) return `+${digits}`;
  return value;
};

/** ISO dates (Meta) and DD.MM.YYYY[ HH:MM] (spreadsheets) */
export const parseDate = (raw: string | undefined): string | null => {
  const value = (raw ?? "").trim();
  if (!value) return null;
  const local = value.match(
    /^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{2}))?/,
  );
  const date = local
    ? new Date(
        Number(local[3]),
        Number(local[2]) - 1,
        Number(local[1]),
        Number(local[4] ?? 0),
        Number(local[5] ?? 0),
      )
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const parseSource = (
  raw: string | undefined,
  fallback: LeadSource,
): LeadSource => {
  const value = (raw ?? "").trim().toLowerCase();
  if (/^(fb|facebook|messenger)$/.test(value)) return "facebook";
  if (/^(ig|instagram)$/.test(value)) return "instagram";
  if (/^(site|web|website|сайт)$/.test(value)) return "site";
  return fallback;
};

const parseRooms = (raw: string | undefined) => {
  const match = (raw ?? "").match(/[1-6]/);
  return match ? Number(match[0]) : null;
};

export type ImportedLead = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string | null;
  created_at: string | null;
  campaign: string | null;
  source_form: string | null;
  source: LeadSource;
  rooms_wanted: number | null;
  utm: Record<string, string> | null;
  comment: string | null;
};

export type ImportPlan = {
  leads: ImportedLead[];
  noPhone: number;
  duplicatesInBase: number;
  duplicatesInFile: number;
};

/**
 * Turn rows into leads. Rows without a usable phone are skipped, as are
 * numbers already in the base or earlier in the file (matched on the last
 * 9 digits).
 */
export const planImport = (
  rows: Record<string, string>[],
  mapping: Mapping,
  existingPhoneKeys: Set<string>,
  defaultSource: LeadSource,
): ImportPlan => {
  const plan: ImportPlan = {
    leads: [],
    noPhone: 0,
    duplicatesInBase: 0,
    duplicatesInFile: 0,
  };
  const seen = new Set<string>();
  const columns = (target: ImportTarget) =>
    Object.keys(mapping).filter((header) => mapping[header] === target);
  const first = (row: Record<string, string>, target: ImportTarget) => {
    const header = columns(target)[0];
    const value = header ? row[header]?.trim() : "";
    return value || undefined;
  };

  for (const row of rows) {
    const phone = normalizePhone(first(row, "phone"));
    const key = phoneKey(phone);
    if (key.length < 9) {
      plan.noPhone++;
      continue;
    }
    if (existingPhoneKeys.has(key)) {
      plan.duplicatesInBase++;
      continue;
    }
    if (seen.has(key)) {
      plan.duplicatesInFile++;
      continue;
    }
    seen.add(key);

    let firstName = first(row, "first_name") ?? "";
    let lastName = first(row, "last_name") ?? "";
    const full = first(row, "full_name");
    if (full && !firstName) {
      const [given, ...rest] = full.split(/\s+/);
      firstName = given;
      lastName = lastName || rest.join(" ");
    }
    const utm: Record<string, string> = {};
    for (const target of [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ] as const) {
      const value = first(row, target);
      if (value) utm[target] = value;
    }
    const answers = columns("comment")
      .map((header) => [header, row[header]?.trim()] as const)
      .filter(([, value]) => value)
      .map(([header, value]) => `${header.replace(/_/g, " ")}: ${value}`);
    const roomsText = first(row, "rooms");

    plan.leads.push({
      first_name: firstName,
      last_name: lastName,
      phone,
      email: first(row, "email") ?? null,
      created_at: parseDate(first(row, "created_at")),
      campaign: first(row, "campaign") ?? null,
      source_form: first(row, "form") ?? null,
      source: parseSource(first(row, "source"), defaultSource),
      rooms_wanted: parseRooms(roomsText),
      utm: Object.keys(utm).length ? utm : null,
      comment:
        [
          ...(roomsText
            ? [`${columns("rooms")[0].replace(/_/g, " ")}: ${roomsText}`]
            : []),
          ...answers,
        ].join("\n") || null,
    });
  }
  return plan;
};
